import 'whatwg-fetch'

import spf from '../spf-parse'
import _ from 'lodash'

class SPFLookup {

  constructor(dnsAPI) {
    this.dnsAPI = dnsAPI;
  }

  check(hostname) {

    let apiURL = this.dnsAPI;

    let lookups = [];
    let ptrHosts = [];

    var lookup = function (hostname, record, count = true) {
      
      return new Promise(function(resolve, reject) {

        // Test if lookup is in cache

        for (let i = 0; i < lookups.length; i++) {

          let l = lookups[i];

          if (l.name === hostname && l.type === record) {
            return resolve(l.answers);
          }
          
        }

        let url = new URL(apiURL);

        url.searchParams.append("name", hostname);
        url.searchParams.append("type", record);

        fetch(url).then((res) => res.json()).then(function(data) {

          let answer =  "Answer" in data ? data.Answer : [];

          lookups.push({"name": hostname, "type": record, "answers": answer, count: count});
      
          if (data.Status === 0 || data.Status === 3) {
            return resolve(answer);
          }

          return reject(new Error(
            `Error (code: ${data.Status}) fetching DNS (${record}) data for: ${hostname}`
          ));

        });

      });

    }

    var checkSPF = function(hostname, count = true) {

      return lookup(hostname, "TXT", count).then(function(answers) {

        return new Promise(function(resolve, reject) {

          // Filter only SPF records

          answers = answers.filter(
            (a) => 
              a.data.startsWith("\"v=spf1 ") || a.data === "\"v=spf1\""
          );

          if (answers.length > 1) {
            return reject(new Error("More than one SPF record found for \"" + hostname +"\""));
          }
          
          if (answers.length === 0) {
            return resolve("");
          }
        
          // The reply will have an extra set of quotes, strip them

          return resolve(answers[0].data.slice(1, -1));

        });

      });

    }

    var checkA = function(hostname, count = true) {

      hostname = hostname.split("/")[0];

      return new Promise(function(resolve, reject) {

        // Expand a mechanisms to both A and AAAA lookups

        Promise.all([
          lookup(hostname, "A", count),
          lookup(hostname, "AAAA", false)
        ]).then(function(answers) {

          // This may contain CNAME replies, so filter out anything that's not
          // A (type 1) or AAAA (type 28)

          resolve(_.flatten(answers).filter(
            (a) => a.type === 1 || a.type === 28
          ));

        })

      });

    }

    var checkMX = function(hostname) {

      return new Promise(function(resolve, reject) {

        lookup(hostname, "MX").then(function (answers) {

          // For each MX result, lookup the corresponding A and 
          // AAAA records

          Promise.all(answers.map(
            a =>
              checkA(a.data.split(" ")[1], false)
          )).then(function(answers) {
            resolve(_.flatten(answers));
          });
        });

      });
                   
    }

    var checkPTR = function(hostname) {

      if (ptrHosts.length === 0) {

        // Add a mock PTR lookup if this is the first PTR mechanism

        lookups.push({"name": "<client-ip>", "type": "PTR", "answers": [{
          type: 12, // type PTR
          data: "<client-reverse-dns-hostname>",
        }]});

      }

      ptrHosts.push(hostname);

      return Promise.resolve(null);
                   
    }

    var checkExists = function(hostname) {

      // For the exists mechanism, only add a mock A lookup, since
      // an exists mechanism will always result in a DNS lookup
      
      lookups.push({"name": hostname, "type": "A", "answers": [], count: true});

      return Promise.resolve(null);
                   
    }
      
    var parseMechanisms = function (domain) {

      // Parse the mechanisms in the record, in the context of a 
      // domain name. 

      return function (record) {
      
        if (record === "") {
          return Promise.resolve([]);
        }

        let parsed = spf(record);

        if (!parsed.valid) {
          return Promise.reject(new Error("Invalid SPF record: " + record));
        }

        return Promise.all(parsed.mechanisms.map(parseMechanism(domain)))
        
      }

    }

    var parseMechanism = function (domain) {

      let all = false;

      return function (mechanism) {

        if (all) {

          // Abort processing after the first 'all' mechanism
          return Promise.resolve(null);

        }

        switch (mechanism.type) {
        
        case "all":
          all = true;
          return Promise.resolve(null);
        
        case "version":
          return Promise.resolve(null);
          
        case "exp":
          return Promise.resolve(null);
          
        case "ip4":
          return Promise.resolve({[domain]: [mechanism.value]});
          
        case "ip6":
          return Promise.resolve({[domain]: [mechanism.value]});
          
        case "include":
          return checkSPF(mechanism.value).then(parseMechanisms(mechanism.value)).then(combine);
          
        case "redirect":
          return checkSPF(mechanism.value).then(parseMechanisms(mechanism.value)).then(combine);
          
        case "a":
          return checkA(mechanism.value || domain).then(formatGroup(domain, mechanism.value));
          
        case "mx":
          return checkMX(mechanism.value || domain).then(formatGroup(domain, mechanism.value));
          
        case "ptr":
          return checkPTR(mechanism.value || domain);
          
        case "exists":
          return checkExists(mechanism.value);
          
        case undefined:
          return Promise.resolve(null);
          
        default:
          return Promise.reject(new Error("cannot handle '" + mechanism.type + "' mechanism yet"));
        
        }

      }

    }

    var formatGroup = function(base, value) {

      let prefix = "";

      if (value && value.indexOf("/") > 0) {
        prefix = "/" + value.split("/")[1];
      }
      
      return function(answers) {
        
        return Promise.resolve({
          [base]: answers.map((a) => a.data + prefix),
        });
        
      }

    }

    var combine = function (results) {

      return new Promise(function(resolve, reject) {
        
        let networks = {}
        
        _.forEach(results, function(result) {
          
          if (result == null) {
            return;
          }
          
          _.forEach(result, function(hostNetworks, host) {
            
            if (!(host in networks)) {
              networks[host] = [];
            }
            
            networks[host] = _.union(networks[host], hostNetworks);
            
          });
          
        });
        
        return resolve(networks);
        
      });
      
    }
    
    let topPolicy = "";

    return new Promise(function(resolve, reject) {
      checkSPF(hostname, false)
        .then(function(policy) {
          topPolicy = policy
          return parseMechanisms(hostname)(policy)
        })
        .then(combine)
        .then(function(networks) {
          return resolve({
            "hostname": hostname,
            "networks": networks, 
            "lookups": lookups, 
            "policy": topPolicy,
            "parsed": spf(topPolicy),
            "ptrHosts": ptrHosts,
            "lookupCount": lookups.filter((l) => l.count).length,
          });
        }).catch(reject);;
    });

  }

}

export default SPFLookup;


// WEBPACK FOOTER //
// ./src/spf-lookup/index.js